import { Component, Input, OnInit } from '@angular/core';
import { INCOME_FORM_FINANCIAL_ASSETS_KEYS } from '../../../../constants/income-form-financial-assets-constant';

@Component({
    selector: 'app-income-from-financial-assets-en-v2',
    templateUrl: './income-from-financial-assets-en-v2.component.html',
    styleUrls: [
        './income-from-financial-assets-en-v2.component.css',
        '../../../shared/return-view-text-fields.css',
    ],
    standalone: false
})
export class IncomeFromFinancialAssetsEnV2Component implements OnInit {
  @Input() returnData: any;
  @Input() printActive: boolean = false;

  incomeFormFinancialAssetsData: any[] = [];

  readonly amountParticulars = INCOME_FORM_FINANCIAL_ASSETS_KEYS
    .filter((k) => k.type === 'amount' && k.scope !== 'parent')
    .slice()
    .sort((a, b) => a.order - b.order);

  readonly parentAmountParticulars = INCOME_FORM_FINANCIAL_ASSETS_KEYS
    .filter((k) => k.type === 'amount' && k.scope === 'parent')
    .slice()
    .sort((a, b) => a.order - b.order);

  readonly sroTextParticulars = INCOME_FORM_FINANCIAL_ASSETS_KEYS
    .filter((k) => k.type === 'text')
    .slice()
    .sort((a, b) => a.order - b.order);

  readonly sroIncomeTextParticulars = [
    { key: 'bondName', title: 'Particulars' },
    { key: 'issueOffice', title: 'Payment Authority' },
    { key: 'registrationNo', title: 'Reference No' },
    { key: 'dateOfIssue', title: 'Date' },
    { key: 'sroNo', title: 'SRO No' },
    { key: 'year', title: 'Year' },
    { key: 'particularSro', title: 'Particular of the SRO' }
  ];

  readonly sroIncomeAmountParticulars = [
    { key: 'grossInterest', title: 'Gross Amount Received' },
    { key: 'relatedExpenses', title: 'Related Expenses' },
    { key: 'value', title: 'Net Income' },
    { key: 'deductedTax', title: 'Income Exempted from Tax' },
    { key: 'taxableIncome', title: 'Taxable Income' },
    { key: 'taxApplicable', title: 'Applicable Tax as per SRO' }
  ];

  constructor() { }

  getValue(item: any, key: string): any {
    return item ? item[key] : null;
  }

  hasValue(item: any, key: string): boolean {
    const val = this.getValue(item, key);
    return val !== null && val !== undefined && val !== '';
  }

  isSroType(iisTypeName: any): boolean {
    const v = iisTypeName === null || iisTypeName === undefined ? '' : String(iisTypeName).trim();
    return v.startsWith('income_exempted_reduced_by_sro');
  }

  isSroIncomeType(iisTypeName: any): boolean {
    const v = iisTypeName === null || iisTypeName === undefined ? '' : String(iisTypeName).trim();
    return v === 'sro_income';
  }

  isSroStyleType(iisTypeName: any): boolean {
    return this.isSroType(iisTypeName) || this.isSroIncomeType(iisTypeName);
  }

  getInterestSecurityDetails(item: any): any[] {
    return Array.isArray(item && item.interestSecurityDetails) ? item.interestSecurityDetails : [];
  }

  getSroTextParticulars(detail: any, iisTypeName?: any): any[] {
    const fields = this.isSroIncomeType(iisTypeName)
      ? this.sroIncomeTextParticulars
      : this.sroTextParticulars;
    return fields.filter((field) => this.hasValue(detail, field.key));
  }

  getSroAmountParticulars(iisTypeName: any): any[] {
    if (this.isSroIncomeType(iisTypeName)) {
      return this.sroIncomeAmountParticulars;
    }
    return this.amountParticulars;
  }

  getSroAmountTitle(particular: any, iisTypeName: any): string {
    if (this.isSroIncomeType(iisTypeName)) {
      return particular.title;
    }
    if (particular.key === 'deductedTax') {
      return 'Tax Exempted Income';
    }
    return particular.title;
  }

  getSroAmountValue(item: any, detail: any, particular: any): any {
    const key = particular ? particular.key : '';
    if (this.isSroIncomeType(item ? item.iisTypeName : '')) {
      if (key === 'taxApplicable') {
        if (this.hasValue(detail, 'taxApplicable')) {
          return this.getValue(detail, 'taxApplicable');
        }
        if (this.getInterestSecurityDetails(item).length === 1) {
          return this.getValue(item, 'applicableTaxSRO');
        }
        return null;
      }
      if (key === 'value') {
        if (this.hasValue(detail, 'value')) {
          return this.getValue(detail, 'value');
        }
        return this.getValue(detail, 'netIncome');
      }
      if (key === 'relatedExpenses') {
        if (this.hasValue(detail, 'relatedExpenses')) {
          return this.getValue(detail, 'relatedExpenses');
        }
        if (this.getInterestSecurityDetails(item).length === 1) {
          return this.getValue(item, 'totalExpense');
        }
        return null;
      }
    }
    return this.getValue(detail, key);
  }

  hasSroAmountValue(item: any, detail: any, particular: any): boolean {
    const val = this.getSroAmountValue(item, detail, particular);
    return val !== null && val !== undefined && val !== '';
  }

  isSanchayapatraType(iisTypeName: any): boolean {
    const v = iisTypeName === null || iisTypeName === undefined ? '' : String(iisTypeName).trim();
    return ['sanchayapatra', 'sanchayapatra_OTP', 'sanchayapatra_pensioners'].includes(v);
  }

  isBankFiType(iisTypeName: any): boolean {
    return String(iisTypeName || '').trim() === 'interest_profit_bank_fi';
  }

  isTreasuryBondType(iisTypeName: any): boolean {
    return String(iisTypeName || '').trim() === 'treasury_bond';
  }

  isDividendType(iisTypeName: any): boolean {
    return String(iisTypeName || '').trim() === 'dividend_others';
  }

  isOtherSecuritiesType(iisTypeName: any): boolean {
    return String(iisTypeName || '').trim() === 'interest_from_other_Securities';
  }

  isBondsAndSecuritiesType(iisTypeName: any): boolean {
    return String(iisTypeName || '').trim() === 'bonds_and_other_securities';
  }

  isSukukType(iisTypeName: any): boolean {
    return String(iisTypeName || '').trim() === 'profit_from_sukuk_and_sharia';
  }

  getFinancialAssetTypeTitle(iisTypeName: any): string {
    const v = iisTypeName === null || iisTypeName === undefined ? '' : String(iisTypeName).trim();

    if (this.isSroType(v)) {
      return 'Securities Subject to Reduced Tax Rate';
    }
    if (this.isSroIncomeType(v)) {
      return 'Income Subject to Reduced Tax Rate';
    }

    switch (v) {
      case 'sanchayapatra':
      case 'sanchayapatra_OTP':
        return 'Interest From Sanchayapatra';
      case 'sanchayapatra_pensioners':
        return 'Interest From Sanchayapatra (Pensioners)';
      case 'interest_profit_bank_fi':
        return 'Interest/Profit (Bank/FI)';
      case 'treasury_bond':
        return 'Interest/Profit/Discount on Treasury Bill/Bond/SUKUK/Other Securities with TDS';
      case 'dividend_others':
        return 'Dividend (Any kind)';
      case 'interest_from_other_Securities':
        return 'Interest From Any Other Securities/Financial Assets';
      case 'bonds_and_other_securities':
        return 'Interest From Treasury Bill/Bond';
      case 'profit_from_sukuk_and_sharia':
        return 'Profit from SUKUK and Sharia';
      default:
        return v;
    }
  }

  isEncashed(detail: any): boolean {
    const yearEndBalance = this.getValue(detail, 'yearEndBalance');
    return yearEndBalance === 0 || yearEndBalance === '0' || yearEndBalance === 0.0;
  }

  getDetailsTotal(details: any[], key: string): number {
    return details.reduce((sum, detail) => {
      const val = Number(this.getValue(detail, key));
      return sum + (isNaN(val) ? 0 : val);
    }, 0);
  }

  /** Net Income = Gross Interest − Related Fees/Charges (or API netIncome when present). */
  getNetIncome = (detail: any): number => {
    if (!detail) {
      return 0;
    }
    const apiNet = this.getValue(detail, 'netIncome');
    if (apiNet !== null && apiNet !== undefined && apiNet !== '') {
      const n = Number(String(apiNet).replace(/,/g, ''));
      return isNaN(n) ? 0 : n;
    }
    const gross = Number(String(this.getValue(detail, 'grossInterest') ?? 0).replace(/,/g, ''));
    const expense = Number(String(this.getValue(detail, 'relatedExpenses') ?? 0).replace(/,/g, ''));
    const g = isNaN(gross) ? 0 : gross;
    const e = isNaN(expense) ? 0 : expense;
    return g - e;
  };

  getDetailsTotalNetIncome = (details: any[]): number => {
    if (!Array.isArray(details)) {
      return 0;
    }
    return details.reduce((sum, detail) => sum + this.getNetIncome(detail), 0);
  };

  ngOnInit(): void {
    const financialAssetData = this.returnData?.incomeData?.financialAssetData;
    this.incomeFormFinancialAssetsData = Array.isArray(financialAssetData)
      ? financialAssetData
      : [];
  }
}
